import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import type { TakeoffOwnerType } from "@/hooks/useDocumentAnnotations";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Trash2,
  Scale,
  Copy,
  Pencil,
  Palette,
  Minus,
  CopyPlus,
  List,
  Ruler,
} from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "@/components/ui/context-menu";
import { TakeoffToolbar, type TakeoffTool } from "./TakeoffToolbar";
import {
  useAnnotations,
  useSaveAnnotation,
  useDeleteAnnotation,
  useUpdateAnnotation,
  useDocumentScale,
  useSaveScale,
  type Annotation,
} from "@/hooks/useDocumentAnnotations";
import { snapToAngle, collectEndpoints, snapToNearbyPoint, convertLinear, convertArea, LINEAR_UNITS, parseFeetInches, type SnapGuide } from "./takeoff-helpers";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

interface TakeoffViewerProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  documentId: string;
  filePath: string;
  fileName: string;
  mimeType: string | null;
  mode?: "dialog" | "embedded";
  ownerType?: TakeoffOwnerType;
  readOnly?: boolean;
  activeGroupId?: string | null;
  hiddenGroupIds?: Set<string>;
  focusAnnotation?: { pageNumber: number; annotationId: string } | null;
  onFocusHandled?: () => void;
}

interface Point {
  x: number;
  y: number;
}

const COLORS = [
  "#EF4444", "#F97316", "#EAB308", "#22C55E",
  "#3B82F6", "#8B5CF6", "#EC4899", "#000000",
];

const LINE_WIDTHS = [1, 2, 3, 5, 8];

function distancePx(a: Point, b: Point): number {
  return Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);
}

function polygonAreaPx(points: Point[]): number {
  let area = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return Math.abs(area / 2);
}

function polygonPerimeterPx(points: Point[]): number {
  let perim = 0;
  for (let i = 0; i < points.length; i++) {
    perim += distancePx(points[i], points[(i + 1) % points.length]);
  }
  return perim;
}

function polylineLengthPx(points: Point[]): number {
  let len = 0;
  for (let i = 0; i < points.length - 1; i++) {
    len += distancePx(points[i], points[i + 1]);
  }
  return len;
}

// Drag-based tools
const DRAG_TOOLS: TakeoffTool[] = ["rectangle", "cloud", "highlight", "ellipse", "arrow"];

// ─── Hit-testing helpers (relative coords) ───

function hitTestAnnotation(ann: Annotation, rel: Point, w: number, h: number): boolean {
  const geo = ann.geometry;
  const threshold = 12; // px threshold for lines

  switch (ann.annotation_type) {
    case "text":
    case "measurement_count": {
      if (!geo.point) return false;
      const dx = (rel.x - geo.point.x) * w;
      const dy = (rel.y - geo.point.y) * h;
      return Math.sqrt(dx * dx + dy * dy) < 20;
    }
    case "rectangle":
    case "cloud":
    case "highlight":
    case "ellipse": {
      if (!geo.points || geo.points.length < 2) return false;
      const minX = Math.min(geo.points[0].x, geo.points[1].x);
      const maxX = Math.max(geo.points[0].x, geo.points[1].x);
      const minY = Math.min(geo.points[0].y, geo.points[1].y);
      const maxY = Math.max(geo.points[0].y, geo.points[1].y);
      return rel.x >= minX && rel.x <= maxX && rel.y >= minY && rel.y <= maxY;
    }
    case "arrow":
    case "measurement_linear":
    case "scale_calibration": {
      if (!geo.points || geo.points.length < 2) return false;
      return pointToSegmentDist(rel, geo.points[0], geo.points[1], w, h) < threshold;
    }
    case "polyline": {
      if (!geo.points || geo.points.length < 2) return false;
      for (let i = 0; i < geo.points.length - 1; i++) {
        if (pointToSegmentDist(rel, geo.points[i], geo.points[i + 1], w, h) < threshold) return true;
      }
      return false;
    }
    case "measurement_area": {
      if (!geo.points || geo.points.length < 3) return false;
      return pointInPolygon(rel, geo.points);
    }
    default:
      return false;
  }
}

function pointToSegmentDist(p: Point, a: Point, b: Point, w: number, h: number): number {
  const px = p.x * w, py = p.y * h;
  const ax = a.x * w, ay = a.y * h;
  const bx = b.x * w, by = b.y * h;
  const dx = bx - ax, dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return distancePx({ x: px, y: py }, { x: ax, y: ay });
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return distancePx({ x: px, y: py }, { x: ax + t * dx, y: ay + t * dy });
}

function pointInPolygon(p: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;
    if ((yi > p.y) !== (yj > p.y) && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function offsetGeometry(geo: any, dx: number, dy: number): any {
  const newGeo = { ...geo };
  if (geo.point) {
    newGeo.point = { x: geo.point.x + dx, y: geo.point.y + dy };
  }
  if (geo.points) {
    newGeo.points = geo.points.map((p: Point) => ({ x: p.x + dx, y: p.y + dy }));
  }
  return newGeo;
}

/** Check if an annotation is hidden by the hiddenGroupIds set */
function isAnnotationHidden(ann: Annotation, hiddenGroupIds?: Set<string>): boolean {
  if (!hiddenGroupIds || hiddenGroupIds.size === 0) return false;
  const groupKey = ann.takeoff_group_id ?? "__unassigned__";
  return hiddenGroupIds.has(groupKey);
}

export const TakeoffViewer = ({
  open = true,
  onOpenChange,
  documentId,
  filePath,
  fileName,
  mimeType,
  mode = "dialog",
  ownerType = "project",
  readOnly = false,
  activeGroupId = null,
  hiddenGroupIds,
  focusAnnotation,
  onFocusHandled,
}: TakeoffViewerProps) => {
  const isMobile = useIsMobile();
  const isPdf = mimeType?.includes("pdf");
  const isImage = mimeType?.startsWith("image");

  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef<Point>({ x: 0, y: 0 });
  const panOrigin = useRef<Point>({ x: 0, y: 0 });

  const [activeTool, setActiveTool] = useState<TakeoffTool>("select");
  const [activeColor, setActiveColor] = useState("#EF4444");
  const [activeLineWidth, setActiveLineWidth] = useState(3);
  const [drawingPoints, setDrawingPoints] = useState<Point[]>([]);
  const [countTotal, setCountTotal] = useState(0);

  // Live mouse position for rubber-band preview
  const mousePos = useRef<Point | null>(null);

  // Drag state for shapes
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Point | null>(null);
  const [dragCurrent, setDragCurrent] = useState<Point | null>(null);

  // Move annotation state
  const [movingAnn, setMovingAnn] = useState<Annotation | null>(null);
  const [moveStart, setMoveStart] = useState<Point | null>(null);
  const [moveDelta, setMoveDelta] = useState<Point>({ x: 0, y: 0 });

  // Canvas context menu
  const [canvasMenu, setCanvasMenu] = useState<{ x: number; y: number; ann: Annotation | null } | null>(null);

  // Inline text editing
  const [textInput, setTextInput] = useState<{ x: number; y: number; rx: number; ry: number } | null>(null);
  const [textValue, setTextValue] = useState("");
  const textRef = useRef<HTMLTextAreaElement>(null);

  // Scale calibration
  const [showScaleInput, setShowScaleInput] = useState(false);
  const [scaleDistance, setScaleDistance] = useState("");
  const [scaleUnit, setScaleUnit] = useState("ft");
  const [pendingScaleLine, setPendingScaleLine] = useState<Point[] | null>(null);

  // Shift key tracking for angle-snap
  const shiftKeyRef = useRef(false);
  // Display unit for conversions (defaults to scale unit)
  const [displayUnit, setDisplayUnit] = useState("ft");
  // Snap guide lines ref (set during mouse move, read during draw)
  const snapGuidesRef = useRef<SnapGuide[]>([]);

  // Undo stack
  const [undoStack, setUndoStack] = useState<string[]>([]);

  // Edit annotation
  const [editingAnnotation, setEditingAnnotation] = useState<Annotation | null>(null);
  const [editLabel, setEditLabel] = useState("");

  // Selected annotation for inspection
  const [selectedAnnId, setSelectedAnnId] = useState<string | null>(null);

  // Highlight flash animation
  const [highlightAnnId, setHighlightAnnId] = useState<string | null>(null);
  const highlightTimerRef = useRef<number | null>(null);
  const highlightStartRef = useRef<number>(0);
  const drawOverlayRef = useRef<() => void>(() => {});

  const baseCanvasRef = useRef<HTMLCanvasElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<any>(null);
  const canvasDims = useRef({ w: 0, h: 0 });

  const [signedUrl, setSignedUrl] = useState<string | null>(null);

  // Data hooks
  const { data: annotations = [] } = useAnnotations(documentId, pageNum, ownerType);
  const { data: scale } = useDocumentScale(documentId, pageNum, ownerType);
  const saveAnnotation = useSaveAnnotation();
  const deleteAnnotation = useDeleteAnnotation();
  const updateAnnotation = useUpdateAnnotation();
  const saveScale = useSaveScale();

  // ─── Visibility filtering ───
  const visibleAnnotations = useMemo(() => {
    if (!hiddenGroupIds || hiddenGroupIds.size === 0) return annotations;
    return annotations.filter((ann) => !isAnnotationHidden(ann, hiddenGroupIds));
  }, [annotations, hiddenGroupIds]);

  // Clear selection if selected annotation becomes hidden
  useEffect(() => {
    if (selectedAnnId && hiddenGroupIds && hiddenGroupIds.size > 0) {
      const selectedAnn = annotations.find((a) => a.id === selectedAnnId);
      if (selectedAnn && isAnnotationHidden(selectedAnn, hiddenGroupIds)) {
        setSelectedAnnId(null);
      }
    }
  }, [hiddenGroupIds, annotations, selectedAnnId]);

  // ─── Focus annotation from external navigation ───
  useEffect(() => {
    if (!focusAnnotation) return;
    if (focusAnnotation.pageNumber !== pageNum) {
      setPageNum(focusAnnotation.pageNumber);
    }
    setSelectedAnnId(focusAnnotation.annotationId);
    onFocusHandled?.();
    // Also trigger highlight flash
    highlightAnnotation(focusAnnotation.annotationId);
  }, [focusAnnotation]);

  // ─── Highlight flash animation (pulsing glow for ~2s) ───
  const highlightAnnotation = useCallback((annId: string) => {
    // Cancel previous timer
    if (highlightTimerRef.current) {
      cancelAnimationFrame(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    setHighlightAnnId(annId);
    highlightStartRef.current = performance.now();

    const DURATION = 2000; // 2 seconds
    const tick = () => {
      const elapsed = performance.now() - highlightStartRef.current;
      if (elapsed >= DURATION) {
        setHighlightAnnId(null);
        highlightTimerRef.current = null;
        return;
      }
      // Force re-draw each frame for smooth animation
      drawOverlayRef.current();
      highlightTimerRef.current = requestAnimationFrame(tick);
    };
    highlightTimerRef.current = requestAnimationFrame(tick);
  }, []);

  // Cleanup highlight timer on unmount
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) cancelAnimationFrame(highlightTimerRef.current);
    };
  }, []);

  // ─── Pan to center an annotation on the viewport ───
  const panToAnnotation = useCallback((ann: Annotation) => {
    const geo = ann.geometry;
    if (!geo?.points?.length) return;
    const cw = canvasDims.current.w;
    const ch = canvasDims.current.h;
    // Compute centroid in absolute canvas coords
    let cx = 0, cy = 0;
    for (const p of geo.points) {
      cx += p.x * cw;
      cy += p.y * ch;
    }
    cx /= geo.points.length;
    cy /= geo.points.length;

    // Get viewport size
    const container = containerRef.current;
    if (!container) return;
    const vw = container.clientWidth;
    const vh = container.clientHeight;

    // Set pan so that centroid is centered in viewport
    setPan({
      x: vw / 2 - cx * zoom,
      y: vh / 2 - cy * zoom,
    });
  }, [zoom]);

  // ─── readOnly enforcement: force tool to select ───
  useEffect(() => {
    if (readOnly && activeTool !== "select") {
      setActiveTool("select");
      setDrawingPoints([]);
      setIsDragging(false);
      setDragStart(null);
      setDragCurrent(null);
    }
  }, [readOnly, activeTool]);

  const MEASUREMENT_TOOLS: TakeoffTool[] = ["measure_linear", "measure_area", "polyline", "count"];

  const handleToolChange = useCallback((tool: TakeoffTool) => {
    if (readOnly && tool !== "select") return;
    // Gate measurement tools behind scale calibration
    if (MEASUREMENT_TOOLS.includes(tool) && !scale) {
      toast.warning("Calibrate scale first", {
        description: "Use the Scale tool to set a known distance before measuring.",
      });
      setActiveTool("scale");
      setDrawingPoints([]);
      setIsDragging(false);
      setDragStart(null);
      setDragCurrent(null);
      return;
    }
    setActiveTool(tool);
    setDrawingPoints([]);
    setIsDragging(false);
    setDragStart(null);
    setDragCurrent(null);
  }, [readOnly, scale]);

  // Get signed URL
  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data, error } = await supabase.storage
        .from("project-documents")
        .createSignedUrl(filePath, 3600);
      if (error || !data?.signedUrl) {
        toast.error("Could not load document");
        return;
      }
      setSignedUrl(data.signedUrl);
    })();
  }, [open, filePath]);

  // Load PDF or image
  useEffect(() => {
    if (!signedUrl || !open) return;
    const canvas = baseCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (isPdf) {
      pdfjsLib.getDocument(signedUrl).promise.then((pdf) => {
        pdfDocRef.current = pdf;
        setTotalPages(pdf.numPages);
        renderPdfPage(pdf, pageNum, canvas, ctx);
      }).catch(() => toast.error("Failed to load PDF"));
    } else if (isImage) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        canvasDims.current = { w: img.width, h: img.height };
        ctx.drawImage(img, 0, 0);
        setTotalPages(1);
      };
      img.src = signedUrl;
    }
  }, [signedUrl, open, isPdf, isImage, pageNum]);

  const renderPdfPage = async (pdf: any, num: number, canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => {
    const page = await pdf.getPage(num);
    const viewport = page.getViewport({ scale: 2 });
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvasDims.current = { w: viewport.width, h: viewport.height };
    await page.render({ canvasContext: ctx, viewport }).promise;
    drawOverlay();
  };

  useEffect(() => {
    if (!pdfDocRef.current || !isPdf) return;
    const canvas = baseCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    renderPdfPage(pdfDocRef.current, pageNum, canvas, ctx);
  }, [pageNum]);

  // Helper: convert event to relative coords
  const eventToRelative = (e: React.MouseEvent): Point | null => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const px = (e.clientX - rect.left) * scaleX;
    const py = (e.clientY - rect.top) * scaleY;
    return { x: px / canvas.width, y: py / canvas.height };
  };

  // Helper: relative to absolute canvas coords
  const toAbs = useCallback((p: Point, w: number, h: number) => ({
    x: p.x * w,
    y: p.y * h,
  }), []);

  // ─── Draw overlay ───
  const drawOverlay = useCallback(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = canvasDims.current.w;
    canvas.height = canvasDims.current.h;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const w = canvas.width;
    const h = canvas.height;

    const drawLabel = (text: string, cx: number, cy: number, color: string) => {
      ctx.font = "bold 14px sans-serif";
      const tm = ctx.measureText(text);
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.fillRect(cx - 4, cy - 13, tm.width + 8, 18);
      ctx.fillStyle = color;
      ctx.fillText(text, cx, cy);
    };

    const drawEndpoint = (p: Point, color: string) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
    };

    // Apply move delta for the annotation being dragged
    const getGeo = (ann: Annotation) => {
      if (movingAnn && ann.id === movingAnn.id) {
        return offsetGeometry(ann.geometry, moveDelta.x, moveDelta.y);
      }
      return ann.geometry;
    };

    // Use visibleAnnotations for rendering
    visibleAnnotations.forEach((ann) => {
      const geo = getGeo(ann);
      const color = ann.color || "#EF4444";
      const lw = geo.line_width || 3;
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = lw;
      ctx.font = "16px sans-serif";
      ctx.setLineDash([]);

      // Dashed outline when dragging
      if (movingAnn && ann.id === movingAnn.id) {
        ctx.setLineDash([6, 4]);
      }

      switch (ann.annotation_type) {
        case "measurement_linear":
        case "scale_calibration": {
          if (geo.points?.length === 2) {
            const a = toAbs(geo.points[0], w, h);
            const b = toAbs(geo.points[1], w, h);
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
            drawEndpoint(a, color);
            drawEndpoint(b, color);
            const angle = Math.atan2(b.y - a.y, b.x - a.x);
            const perp = angle + Math.PI / 2;
            const tickLen = 8;
            [a, b].forEach((p) => {
              ctx.beginPath();
              ctx.moveTo(p.x - tickLen * Math.cos(perp), p.y - tickLen * Math.sin(perp));
              ctx.lineTo(p.x + tickLen * Math.cos(perp), p.y + tickLen * Math.sin(perp));
              ctx.stroke();
            });
            if (ann.label) {
              const mx = (a.x + b.x) / 2;
              const my = (a.y + b.y) / 2;
              drawLabel(ann.label, mx - 20, my - 4, color);
            }
          }
          break;
        }
        case "polyline": {
          if (geo.points?.length >= 2) {
            ctx.beginPath();
            const first = toAbs(geo.points[0], w, h);
            ctx.moveTo(first.x, first.y);
            geo.points.slice(1).forEach((p: any) => {
              const ap = toAbs(p, w, h);
              ctx.lineTo(ap.x, ap.y);
            });
            ctx.stroke();
            geo.points.forEach((p: any) => drawEndpoint(toAbs(p, w, h), color));
            if (ann.label) {
              const mid = toAbs(geo.points[Math.floor(geo.points.length / 2)], w, h);
              drawLabel(ann.label, mid.x, mid.y - 10, color);
            }
          }
          break;
        }
        case "measurement_area": {
          if (geo.points?.length >= 3) {
            ctx.beginPath();
            const first = toAbs(geo.points[0], w, h);
            ctx.moveTo(first.x, first.y);
            geo.points.slice(1).forEach((p: any) => {
              const ap = toAbs(p, w, h);
              ctx.lineTo(ap.x, ap.y);
            });
            ctx.closePath();
            ctx.save();
            ctx.clip();
            ctx.globalAlpha = 0.12;
            ctx.fillRect(0, 0, w, h);
            const step = 12;
            ctx.globalAlpha = 0.2;
            ctx.lineWidth = 1;
            for (let i = -h; i < w + h; i += step) {
              ctx.beginPath();
              ctx.moveTo(i, 0);
              ctx.lineTo(i + h, h);
              ctx.stroke();
            }
            ctx.restore();
            ctx.globalAlpha = 1;
            ctx.lineWidth = lw;
            ctx.strokeStyle = color;
            ctx.beginPath();
            const f2 = toAbs(geo.points[0], w, h);
            ctx.moveTo(f2.x, f2.y);
            geo.points.slice(1).forEach((p: any) => ctx.lineTo(toAbs(p, w, h).x, toAbs(p, w, h).y));
            ctx.closePath();
            ctx.stroke();
            geo.points.forEach((p: any) => drawEndpoint(toAbs(p, w, h), color));
            if (ann.label) {
              const cx = geo.points.reduce((s: number, p: any) => s + p.x, 0) / geo.points.length * w;
              const cy = geo.points.reduce((s: number, p: any) => s + p.y, 0) / geo.points.length * h;
              drawLabel(ann.label, cx - 30, cy, color);
            }
          }
          break;
        }
        case "measurement_count": {
          if (geo.point) {
            const p = toAbs(geo.point, w, h);
            ctx.beginPath();
            ctx.arc(p.x, p.y, 14, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = "white";
            ctx.font = "bold 14px sans-serif";
            ctx.textAlign = "center";
            ctx.fillText(ann.label || "•", p.x, p.y + 5);
            ctx.textAlign = "start";
          }
          break;
        }
        case "text": {
          if (geo.point && ann.label) {
            const p = toAbs(geo.point, w, h);
            ctx.font = "bold 18px sans-serif";
            const lines = ann.label.split("\n");
            let maxW = 0;
            lines.forEach((l) => { maxW = Math.max(maxW, ctx.measureText(l).width); });
            ctx.fillStyle = "rgba(255,255,255,0.8)";
            ctx.fillRect(p.x - 2, p.y - 16, maxW + 8, lines.length * 22 + 4);
            ctx.fillStyle = color;
            lines.forEach((l, i) => ctx.fillText(l, p.x, p.y + i * 22));
          }
          break;
        }
        case "arrow": {
          if (geo.points?.length === 2) {
            const a = toAbs(geo.points[0], w, h);
            const b = toAbs(geo.points[1], w, h);
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
            const angle = Math.atan2(b.y - a.y, b.x - a.x);
            const hs = 16;
            ctx.beginPath();
            ctx.moveTo(b.x, b.y);
            ctx.lineTo(b.x - hs * Math.cos(angle - 0.4), b.y - hs * Math.sin(angle - 0.4));
            ctx.lineTo(b.x - hs * Math.cos(angle + 0.4), b.y - hs * Math.sin(angle + 0.4));
            ctx.closePath();
            ctx.fill();
          }
          break;
        }
        case "rectangle": {
          if (geo.points?.length === 2) {
            const a = toAbs(geo.points[0], w, h);
            const b = toAbs(geo.points[1], w, h);
            ctx.globalAlpha = 0.1;
            ctx.fillRect(a.x, a.y, b.x - a.x, b.y - a.y);
            ctx.globalAlpha = 1;
            ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
          }
          break;
        }
        case "ellipse": {
          if (geo.points?.length === 2) {
            const a = toAbs(geo.points[0], w, h);
            const b = toAbs(geo.points[1], w, h);
            const cx = (a.x + b.x) / 2;
            const cy = (a.y + b.y) / 2;
            const rx = Math.abs(b.x - a.x) / 2;
            const ry = Math.abs(b.y - a.y) / 2;
            ctx.beginPath();
            ctx.ellipse(cx, cy, Math.max(1, rx), Math.max(1, ry), 0, 0, Math.PI * 2);
            ctx.globalAlpha = 0.1;
            ctx.fill();
            ctx.globalAlpha = 1;
            ctx.stroke();
          }
          break;
        }
        case "cloud": {
          if (geo.points?.length === 2) {
            const a = toAbs(geo.points[0], w, h);
            const b = toAbs(geo.points[1], w, h);
            const minX = Math.min(a.x, b.x), minY = Math.min(a.y, b.y);
            const maxX = Math.max(a.x, b.x), maxY = Math.max(a.y, b.y);
            const rw = maxX - minX, rh = maxY - minY;
            ctx.globalAlpha = 0.08;
            ctx.fillRect(minX, minY, rw, rh);
            ctx.globalAlpha = 1;
            const scallop = 14;
            ctx.beginPath();
            for (let x = minX; x < maxX; x += scallop) {
              ctx.arc(x + scallop / 2, minY, scallop / 2, Math.PI, 0);
            }
            for (let y = minY; y < maxY; y += scallop) {
              ctx.arc(maxX, y + scallop / 2, scallop / 2, -Math.PI / 2, Math.PI / 2);
            }
            for (let x = maxX; x > minX; x -= scallop) {
              ctx.arc(x - scallop / 2, maxY, scallop / 2, 0, Math.PI);
            }
            for (let y = maxY; y > minY; y -= scallop) {
              ctx.arc(minX, y - scallop / 2, scallop / 2, Math.PI / 2, -Math.PI / 2);
            }
            ctx.stroke();
          }
          break;
        }
        case "highlight": {
          if (geo.points?.length === 2) {
            const a = toAbs(geo.points[0], w, h);
            const b = toAbs(geo.points[1], w, h);
            ctx.globalAlpha = 0.3;
            ctx.fillRect(a.x, a.y, b.x - a.x, b.y - a.y);
            ctx.globalAlpha = 1;
          }
          break;
        }
      }
    });

    // ─── Highlight flash for selected/focused annotation ───
    if (highlightAnnId) {
      const hlAnn = visibleAnnotations.find((a) => a.id === highlightAnnId);
      if (hlAnn) {
        const geo = getGeo(hlAnn);
        const elapsed = performance.now() - highlightStartRef.current;
        // Pulsing opacity: oscillates 3 times over 2 seconds
        const pulse = Math.sin((elapsed / 2000) * Math.PI * 6);
        const alpha = 0.25 + 0.35 * Math.max(0, pulse);

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = "#FACC15"; // yellow highlight
        ctx.fillStyle = "rgba(250, 204, 21, 0.3)";
        ctx.lineWidth = 6;
        ctx.shadowColor = "#FACC15";
        ctx.shadowBlur = 18;
        ctx.setLineDash([]);

        const pts = (geo.points || []).map((p: any) => toAbs(p, w, h));

        if (pts.length === 2 && (hlAnn.annotation_type === "measurement_linear" || hlAnn.annotation_type === "scale_calibration")) {
          // Line: draw thick glowing line
          ctx.beginPath();
          ctx.moveTo(pts[0].x, pts[0].y);
          ctx.lineTo(pts[1].x, pts[1].y);
          ctx.stroke();
        } else if (pts.length >= 2) {
          // Polyline or polygon
          ctx.beginPath();
          ctx.moveTo(pts[0].x, pts[0].y);
          for (let i = 1; i < pts.length; i++) {
            ctx.lineTo(pts[i].x, pts[i].y);
          }
          if (hlAnn.annotation_type === "measurement_area" || hlAnn.annotation_type === "cloud") {
            ctx.closePath();
            ctx.fill();
          }
          ctx.stroke();
        } else if (pts.length === 1 && hlAnn.annotation_type === "count_marker") {
          ctx.beginPath();
          ctx.arc(pts[0].x, pts[0].y, 20, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        } else if (pts.length === 2 && (hlAnn.annotation_type === "rectangle" || hlAnn.annotation_type === "highlight")) {
          const a = pts[0], b = pts[1];
          ctx.fillRect(a.x, a.y, b.x - a.x, b.y - a.y);
          ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
        }

        ctx.restore();
      }
    }

    // Draw snap guide lines — only when not readOnly
    if (!readOnly && snapGuidesRef.current.length > 0 && drawingPoints.length > 0) {
      ctx.save();
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = "#00BCD4";
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.5;
      const drawnGuides = new Set<string>();
      snapGuidesRef.current.forEach((g) => {
        const key = `${g.axis}-${Math.round(g.value)}`;
        if (drawnGuides.has(key)) return;
        drawnGuides.add(key);
        ctx.beginPath();
        if (g.axis === "x") {
          ctx.moveTo(g.value, 0);
          ctx.lineTo(g.value, h);
        } else {
          ctx.moveTo(0, g.value);
          ctx.lineTo(w, g.value);
        }
        ctx.stroke();
      });
      ctx.restore();
    }

    // Draw in-progress drawing with rubber-band line to cursor — only when not readOnly
    if (!readOnly) {
      ctx.setLineDash([6, 3]);
      ctx.strokeStyle = activeColor;
      ctx.fillStyle = activeColor;
      ctx.lineWidth = activeLineWidth;

      const cursorRel = mousePos.current;

      // Auto-close indicator for area tool
      if (activeTool === "measure_area" && drawingPoints.length >= 3 && cursorRel) {
        const firstAbs = toAbs(drawingPoints[0], w, h);
        const curAbs = toAbs(cursorRel, w, h);
        if (distancePx(firstAbs, curAbs) < 15) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(firstAbs.x, firstAbs.y, 10, 0, Math.PI * 2);
          ctx.strokeStyle = "#22C55E";
          ctx.lineWidth = 2;
          ctx.setLineDash([]);
          ctx.stroke();
          ctx.fillStyle = "rgba(34, 197, 94, 0.2)";
          ctx.fill();
          ctx.restore();
          ctx.setLineDash([6, 3]);
          ctx.strokeStyle = activeColor;
          ctx.lineWidth = activeLineWidth;
        }
      }

      if (drawingPoints.length > 0) {
        if (activeTool === "measure_linear" || activeTool === "scale") {
          if (drawingPoints.length === 1) {
            const p = toAbs(drawingPoints[0], w, h);
            drawEndpoint(p, activeColor);
            if (cursorRel) {
              const c = toAbs(cursorRel, w, h);
              ctx.beginPath();
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(c.x, c.y);
              ctx.stroke();
              drawEndpoint(c, activeColor);
              const pxDist = distancePx(p, c);
              let lbl = `${pxDist.toFixed(0)} px`;
              if (scale) {
                const realDist = pxDist / scale.pixels_per_unit;
                lbl = `${realDist.toFixed(2)} ${scale.unit}`;
              }
              const mx = (p.x + c.x) / 2;
              const my = (p.y + c.y) / 2;
              drawLabel(lbl, mx - 20, my - 4, activeColor);
            }
          }
        } else if (activeTool === "measure_area" || activeTool === "polyline") {
          ctx.beginPath();
          const first = toAbs(drawingPoints[0], w, h);
          ctx.moveTo(first.x, first.y);
          drawingPoints.slice(1).forEach((p) => ctx.lineTo(toAbs(p, w, h).x, toAbs(p, w, h).y));
          if (cursorRel) {
            const c = toAbs(cursorRel, w, h);
            ctx.lineTo(c.x, c.y);
          }
          ctx.stroke();
          drawingPoints.forEach((p) => drawEndpoint(toAbs(p, w, h), activeColor));
        }
      }

      // Drag ghost preview
      if (isDragging && dragStart && dragCurrent && DRAG_TOOLS.includes(activeTool)) {
        const a = toAbs(dragStart, w, h);
        const b = toAbs(dragCurrent, w, h);
        if (activeTool === "rectangle") {
          ctx.globalAlpha = 0.1;
          ctx.fillRect(a.x, a.y, b.x - a.x, b.y - a.y);
          ctx.globalAlpha = 1;
          ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
        } else if (activeTool === "ellipse") {
          const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
          const rx = Math.abs(b.x - a.x) / 2, ry = Math.abs(b.y - a.y) / 2;
          ctx.beginPath();
          ctx.ellipse(cx, cy, Math.max(1, rx), Math.max(1, ry), 0, 0, Math.PI * 2);
          ctx.globalAlpha = 0.1;
          ctx.fill();
          ctx.globalAlpha = 1;
          ctx.stroke();
        } else if (activeTool === "cloud") {
          ctx.globalAlpha = 0.08;
          ctx.fillRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
          ctx.globalAlpha = 1;
          ctx.strokeRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
        } else if (activeTool === "highlight") {
          ctx.globalAlpha = 0.3;
          ctx.fillRect(a.x, a.y, b.x - a.x, b.y - a.y);
          ctx.globalAlpha = 1;
        } else if (activeTool === "arrow") {
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
          const angle = Math.atan2(b.y - a.y, b.x - a.x);
          const hs = 16;
          ctx.beginPath();
          ctx.moveTo(b.x, b.y);
          ctx.lineTo(b.x - hs * Math.cos(angle - 0.4), b.y - hs * Math.sin(angle - 0.4));
          ctx.lineTo(b.x - hs * Math.cos(angle + 0.4), b.y - hs * Math.sin(angle + 0.4));
          ctx.closePath();
          ctx.fill();
        }
      }
    }

    ctx.setLineDash([]);
  }, [visibleAnnotations, drawingPoints, activeTool, activeColor, activeLineWidth, isDragging, dragStart, dragCurrent, toAbs, movingAnn, moveDelta, scale, readOnly, highlightAnnId]);

  useEffect(() => {
    drawOverlay();
  }, [drawOverlay]);

  // Keep drawOverlayRef current for animation frame callbacks
  drawOverlayRef.current = drawOverlay;

  // ─── Mouse handlers for overlay canvas ───

  const handleOverlayMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (e.button !== 0) return;
    const rel = eventToRelative(e);
    if (!rel) return;

    // In select mode, check for hit-testing (selection allowed in readOnly, moving is not)
    if (activeTool === "select") {
      const w = canvasDims.current.w;
      const h = canvasDims.current.h;
      // Search visible annotations only (reverse order = top-most first)
      for (let i = visibleAnnotations.length - 1; i >= 0; i--) {
        if (hitTestAnnotation(visibleAnnotations[i], rel, w, h)) {
          setSelectedAnnId(visibleAnnotations[i].id);
          if (!readOnly) {
            // Only allow moving in edit mode
            setMovingAnn(visibleAnnotations[i]);
            setMoveStart(rel);
            setMoveDelta({ x: 0, y: 0 });
          }
          e.stopPropagation();
          return;
        }
      }
      // Click on empty space clears selection
      setSelectedAnnId(null);
      return;
    }

    // Block all non-select interactions in readOnly
    if (readOnly) return;

    if (DRAG_TOOLS.includes(activeTool)) {
      setIsDragging(true);
      setDragStart(rel);
      setDragCurrent(rel);
      e.stopPropagation();
    }
  }, [activeTool, visibleAnnotations, readOnly]);

  const handleOverlayMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    let rel = eventToRelative(e);
    if (!rel) return;

    const isDrawingTool = !readOnly && drawingPoints.length > 0 && (activeTool === "measure_linear" || activeTool === "scale" || activeTool === "measure_area" || activeTool === "polyline");

    // Apply snaps for drawing tools — snap to visible annotations only
    if (isDrawingTool) {
      const lastPt = drawingPoints[drawingPoints.length - 1];
      const cw = canvasDims.current.w;
      const ch = canvasDims.current.h;

      // Shift-snap to 45° angles
      if (shiftKeyRef.current) {
        rel = snapToAngle(lastPt, rel, cw, ch);
      }

      // Snap to visible annotation endpoints only
      const endpoints = collectEndpoints(visibleAnnotations);
      const { snapped, guides } = snapToNearbyPoint(rel, endpoints, cw, ch);
      rel = snapped;
      snapGuidesRef.current = guides;
    } else {
      snapGuidesRef.current = [];
    }

    mousePos.current = rel;

    if (!readOnly && movingAnn && moveStart) {
      setMoveDelta({ x: rel.x - moveStart.x, y: rel.y - moveStart.y });
      return;
    }
    if (!readOnly && isDragging && dragStart) {
      setDragCurrent(rel);
      return;
    }

    // Trigger redraw for rubber-band line during measurement tools
    if (isDrawingTool) {
      drawOverlay();
    }
  }, [isDragging, dragStart, movingAnn, moveStart, drawingPoints, activeTool, drawOverlay, visibleAnnotations, readOnly]);

  const handleOverlayMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    // Block all mutation in readOnly
    if (readOnly) {
      setMovingAnn(null);
      setMoveStart(null);
      setMoveDelta({ x: 0, y: 0 });
      setIsDragging(false);
      setDragStart(null);
      setDragCurrent(null);
      return;
    }

    // Finish moving annotation
    if (movingAnn && moveStart) {
      const dx = moveDelta.x;
      const dy = moveDelta.y;
      if (Math.abs(dx) > 0.001 || Math.abs(dy) > 0.001) {
        const newGeo = offsetGeometry(movingAnn.geometry, dx, dy);
        updateAnnotation.mutate({
          id: movingAnn.id,
          documentId: documentId,
          page_number: pageNum,
          geometry: newGeo,
        });
      }
      setMovingAnn(null);
      setMoveStart(null);
      setMoveDelta({ x: 0, y: 0 });
      return;
    }

    if (isDragging && dragStart && dragCurrent) {
      const dist = distancePx(
        { x: dragStart.x * canvasDims.current.w, y: dragStart.y * canvasDims.current.h },
        { x: dragCurrent.x * canvasDims.current.w, y: dragCurrent.y * canvasDims.current.h }
      );
      if (dist > 5) {
        saveAnnotation.mutate({
          documentId: documentId, ownerType, takeoff_group_id: activeGroupId,
          page_number: pageNum,
          annotation_type: activeTool,
          geometry: { points: [dragStart, dragCurrent], line_width: activeLineWidth },
          color: activeColor,
        });
      }
      setIsDragging(false);
      setDragStart(null);
      setDragCurrent(null);
    }
  }, [isDragging, dragStart, dragCurrent, activeTool, activeColor, activeLineWidth, documentId, pageNum, saveAnnotation, movingAnn, moveStart, moveDelta, updateAnnotation, readOnly]);

  // Canvas right-click — only hit-test visible annotations
  const handleCanvasContextMenu = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const rel = eventToRelative(e);
    if (!rel) return;
    const w = canvasDims.current.w;
    const h = canvasDims.current.h;
    let hitAnn: Annotation | null = null;
    for (let i = visibleAnnotations.length - 1; i >= 0; i--) {
      if (hitTestAnnotation(visibleAnnotations[i], rel, w, h)) {
        hitAnn = visibleAnnotations[i];
        break;
      }
    }
    setCanvasMenu({ x: e.clientX, y: e.clientY, ann: hitAnn });
  }, [visibleAnnotations]);

  // Canvas click handler (non-drag tools)
  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      // Close canvas context menu
      if (canvasMenu) {
        setCanvasMenu(null);
        return;
      }

      // Block all creation in readOnly
      if (readOnly) return;

      if (DRAG_TOOLS.includes(activeTool)) return;

      const rel = eventToRelative(e);
      if (!rel) return;
      const { x: rx, y: ry } = rel;
      const canvas = overlayCanvasRef.current!;

      if (activeTool === "select") return;

      // Block measurement tools without calibration (safety net)
      if (MEASUREMENT_TOOLS.includes(activeTool) && !scale) {
        toast.warning("Calibrate scale first");
        setActiveTool("scale");
        return;
      }

      // Shared finalize logic for polyline/area
      const finalizeMultiPoint = () => {
        const canvas = overlayCanvasRef.current;
        if (!canvas) return;
        if (activeTool === "polyline" && drawingPoints.length >= 2) {
          const absPts = drawingPoints.map((p) => ({ x: p.x * canvas.width, y: p.y * canvas.height }));
          const lenPx = polylineLengthPx(absPts);
          let label = `${lenPx.toFixed(0)} px`;
          let measuredValue: number | undefined;
          if (scale) {
            const real = lenPx / scale.pixels_per_unit;
            label = `${real.toFixed(2)} ${scale.unit}`;
            measuredValue = real;
          }
          saveAnnotation.mutate({
            documentId: documentId, ownerType, takeoff_group_id: activeGroupId,
            page_number: pageNum,
            annotation_type: "polyline",
            geometry: { points: drawingPoints, line_width: activeLineWidth },
            label,
            color: activeColor,
            measured_value: measuredValue,
            unit: scale?.unit || "px",
          });
          setDrawingPoints([]);
        }
        if (activeTool === "measure_area" && drawingPoints.length >= 3) {
          const absPts = drawingPoints.map((p) => ({ x: p.x * canvas.width, y: p.y * canvas.height }));
          const areaPx = polygonAreaPx(absPts);
          const perimPx = polygonPerimeterPx(absPts);
          let label = `${areaPx.toFixed(0)} px²`;
          let measuredValue: number | undefined;
          if (scale) {
            const realArea = areaPx / (scale.pixels_per_unit ** 2);
            const realPerim = perimPx / scale.pixels_per_unit;
            label = `${realArea.toFixed(2)} sq ${scale.unit} | P: ${realPerim.toFixed(2)} ${scale.unit}`;
            measuredValue = realArea;
          }
          saveAnnotation.mutate({
            documentId: documentId, ownerType, takeoff_group_id: activeGroupId,
            page_number: pageNum,
            annotation_type: "measurement_area",
            geometry: { points: drawingPoints, line_width: activeLineWidth },
            label,
            color: activeColor,
            measured_value: measuredValue,
            unit: scale?.unit || "px",
          });
          setDrawingPoints([]);
        }
      };

      // Snap helper
      const snapClick = (pt: Point): Point => {
        const cw = canvasDims.current.w;
        const ch = canvasDims.current.h;
        let snapped = pt;
        if (shiftKeyRef.current && drawingPoints.length > 0) {
          snapped = snapToAngle(drawingPoints[drawingPoints.length - 1], snapped, cw, ch);
        }
        const endpoints = collectEndpoints(visibleAnnotations);
        const result = snapToNearbyPoint(snapped, endpoints, cw, ch);
        return result.snapped;
      };

      if (activeTool === "count") {
        const num = countTotal + 1;
        setCountTotal(num);
        saveAnnotation.mutate({
          documentId: documentId, ownerType, takeoff_group_id: activeGroupId,
          page_number: pageNum,
          annotation_type: "measurement_count",
          geometry: { point: { x: rx, y: ry } },
          label: String(num),
          color: activeColor,
        });
        return;
      }

      if (activeTool === "text") {
        const rect = canvas.getBoundingClientRect();
        const screenX = rx * rect.width;
        const screenY = ry * rect.height;
        setTextInput({ x: screenX, y: screenY, rx, ry });
        setTextValue("");
        setTimeout(() => textRef.current?.focus(), 50);
        return;
      }

      // Two-point measurement tools
      if (activeTool === "measure_linear" || activeTool === "scale") {
        const clickPt = snapClick({ x: rx, y: ry });
        if (drawingPoints.length === 0) {
          setDrawingPoints([clickPt]);
        } else {
          const pts = [...drawingPoints, clickPt];

          if (activeTool === "scale") {
            setPendingScaleLine(pts);
            // Auto-fill distance if we already have a scale
            if (scale) {
              const pxDist = distancePx(
                { x: pts[0].x * canvas.width, y: pts[0].y * canvas.height },
                { x: pts[1].x * canvas.width, y: pts[1].y * canvas.height }
              );
              const realDist = pxDist / scale.pixels_per_unit;
              setScaleDistance(realDist.toFixed(2));
            }
            setShowScaleInput(true);
            setDrawingPoints([]);
            return;
          }

          const pxDist = distancePx(
            { x: pts[0].x * canvas.width, y: pts[0].y * canvas.height },
            { x: pts[1].x * canvas.width, y: pts[1].y * canvas.height }
          );
          let label = `${pxDist.toFixed(0)} px`;
          let measuredValue: number | undefined;
          if (scale) {
            const realDist = pxDist / scale.pixels_per_unit;
            label = `${realDist.toFixed(2)} ${scale.unit}`;
            measuredValue = realDist;
          }
          saveAnnotation.mutate({
            documentId: documentId, ownerType, takeoff_group_id: activeGroupId,
            page_number: pageNum,
            annotation_type: "measurement_linear",
            geometry: { points: pts, line_width: activeLineWidth },
            label,
            color: activeColor,
            measured_value: measuredValue,
            unit: scale?.unit || "px",
          });
          setDrawingPoints([]);
        }
        return;
      }

      // Polyline – multi-click, Enter or double-click to finish
      if (activeTool === "polyline") {
        const clickPt = snapClick({ x: rx, y: ry });
        setDrawingPoints((prev) => [...prev, clickPt]);
        return;
      }

      // Area tool – multi-click, auto-close near start, Enter or double-click to finish
      if (activeTool === "measure_area") {
        const clickPt = snapClick({ x: rx, y: ry });
        // Auto-close when clicking near first point with ≥3 points
        if (drawingPoints.length >= 3) {
          const firstAbs = { x: drawingPoints[0].x * canvasDims.current.w, y: drawingPoints[0].y * canvasDims.current.h };
          const clickAbs = { x: clickPt.x * canvasDims.current.w, y: clickPt.y * canvasDims.current.h };
          if (distancePx(firstAbs, clickAbs) < 15) {
            finalizeMultiPoint();
            return;
          }
        }
        setDrawingPoints((prev) => [...prev, clickPt]);
        return;
      }
    },
    [activeTool, drawingPoints, activeColor, activeLineWidth, documentId, pageNum, scale, countTotal, saveAnnotation, canvasMenu, visibleAnnotations, readOnly]
  );

  // Double-click to finish polyline or area
  const handleCanvasDblClick = useCallback(() => {
    if (readOnly) return;
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;

    if (activeTool === "polyline" && drawingPoints.length >= 2) {
      const absPts = drawingPoints.map((p) => ({ x: p.x * canvas.width, y: p.y * canvas.height }));
      const lenPx = polylineLengthPx(absPts);
      let label = `${lenPx.toFixed(0)} px`;
      let measuredValue: number | undefined;
      if (scale) {
        const real = lenPx / scale.pixels_per_unit;
        label = `${real.toFixed(2)} ${scale.unit}`;
        measuredValue = real;
      }
      saveAnnotation.mutate({
        documentId: documentId, ownerType, takeoff_group_id: activeGroupId,
        page_number: pageNum,
        annotation_type: "polyline",
        geometry: { points: drawingPoints, line_width: activeLineWidth },
        label,
        color: activeColor,
        measured_value: measuredValue,
        unit: scale?.unit || "px",
      });
      setDrawingPoints([]);
    }

    if (activeTool === "measure_area" && drawingPoints.length >= 3) {
      const absPts = drawingPoints.map((p) => ({ x: p.x * canvas.width, y: p.y * canvas.height }));
      const areaPx = polygonAreaPx(absPts);
      const perimPx = polygonPerimeterPx(absPts);
      let label = `${areaPx.toFixed(0)} px²`;
      let measuredValue: number | undefined;
      if (scale) {
        const realArea = areaPx / (scale.pixels_per_unit ** 2);
        const realPerim = perimPx / scale.pixels_per_unit;
        label = `${realArea.toFixed(2)} sq ${scale.unit} | P: ${realPerim.toFixed(2)} ${scale.unit}`;
        measuredValue = realArea;
      }
      saveAnnotation.mutate({
        documentId: documentId, ownerType, takeoff_group_id: activeGroupId,
        page_number: pageNum,
        annotation_type: "measurement_area",
        geometry: { points: drawingPoints, line_width: activeLineWidth },
        label,
        color: activeColor,
        measured_value: measuredValue,
        unit: scale?.unit || "px",
      });
      setDrawingPoints([]);
    }
  }, [activeTool, drawingPoints, activeLineWidth, scale, documentId, pageNum, activeColor, saveAnnotation, readOnly]);

  // Commit text annotation
  const commitText = useCallback(() => {
    if (readOnly) { setTextInput(null); setTextValue(""); return; }
    if (!textInput || !textValue.trim()) {
      setTextInput(null);
      setTextValue("");
      return;
    }
    saveAnnotation.mutate({
      documentId: documentId, ownerType, takeoff_group_id: activeGroupId,
      page_number: pageNum,
      annotation_type: "text",
      geometry: { point: { x: textInput.rx, y: textInput.ry } },
      label: textValue.trim(),
      color: activeColor,
    });
    setTextInput(null);
    setTextValue("");
  }, [textInput, textValue, documentId, pageNum, activeColor, saveAnnotation, readOnly]);

  // Scale calibration submit
  const handleScaleSubmit = () => {
    if (readOnly) return;
    if (!pendingScaleLine || !scaleDistance) return;
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;

    let dist: number;
    if (scaleUnit === "ft" || scaleUnit === "in") {
      const parsed = parseFeetInches(scaleDistance);
      if (parsed === null || parsed <= 0) {
        toast.error("Enter a valid distance (e.g. 5'11\" or 10.5)");
        return;
      }
      // parseFeetInches always returns decimal feet
      if (scaleUnit === "in") {
        dist = parsed * 12; // convert feet to inches
      } else {
        dist = parsed;
      }
    } else {
      dist = parseFloat(scaleDistance);
      if (isNaN(dist) || dist <= 0) {
        toast.error("Enter a valid distance");
        return;
      }
    }

    const pxDist = distancePx(
      { x: pendingScaleLine[0].x * canvas.width, y: pendingScaleLine[0].y * canvas.height },
      { x: pendingScaleLine[1].x * canvas.width, y: pendingScaleLine[1].y * canvas.height }
    );
    const ppu = pxDist / dist;
    saveScale.mutate({
      documentId: documentId, ownerType,
      page_number: pageNum,
      pixels_per_unit: ppu,
      unit: scaleUnit,
    });
    // Delete old scale calibration annotations for this page before saving new one
    const oldCalibrations = annotations.filter(
      (a) => a.annotation_type === "scale_calibration" && a.page_number === pageNum
    );
    oldCalibrations.forEach((old) => {
      deleteAnnotation.mutate({ id: old.id, documentId: documentId, page_number: pageNum });
    });
    saveAnnotation.mutate({
      documentId: documentId, ownerType, takeoff_group_id: activeGroupId,
      page_number: pageNum,
      annotation_type: "scale_calibration",
      geometry: { points: pendingScaleLine },
      label: `${dist} ${scaleUnit}`,
      color: "#22C55E",
    });
    setShowScaleInput(false);
    setPendingScaleLine(null);
    setScaleDistance("");
    setActiveTool("select");
  };

  // Pan with middle mouse, right-click (any tool), or select tool left-click
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 1 || e.button === 2 || (activeTool === "select" && e.button === 0 && !movingAnn)) {
      setIsPanning(true);
      panStart.current = { x: e.clientX, y: e.clientY };
      panOrigin.current = { ...pan };
      e.preventDefault();
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      setPan({
        x: panOrigin.current.x + (e.clientX - panStart.current.x),
        y: panOrigin.current.y + (e.clientY - panStart.current.y),
      });
    }
  };

  const handleMouseUp = () => setIsPanning(false);

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((oldZoom) => {
      const newZoom = Math.max(0.2, Math.min(5, oldZoom + delta));
      const worldX = (cursorX - pan.x) / oldZoom;
      const worldY = (cursorY - pan.y) / oldZoom;
      setPan({ x: cursorX - worldX * newZoom, y: cursorY - worldY * newZoom });
      return newZoom;
    });
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      // Shift tracking
      if (e.key === "Shift") shiftKeyRef.current = true;

      if (e.key === "Escape") {
        if (canvasMenu) {
          setCanvasMenu(null);
        } else if (textInput) {
          setTextInput(null);
          setTextValue("");
        } else {
          setActiveTool("select");
          setDrawingPoints([]);
        }
      }

      // Block creation/edit shortcuts in readOnly
      if (readOnly) return;

      // Enter to finalize polyline/area
      if (e.key === "Enter" && !e.ctrlKey && !e.metaKey) {
        if (document.activeElement?.tagName === "INPUT" || document.activeElement?.tagName === "TEXTAREA") return;
        if ((activeTool === "polyline" && drawingPoints.length >= 2) ||
            (activeTool === "measure_area" && drawingPoints.length >= 3)) {
          handleCanvasDblClick();
        }
      }
      if (e.key === "t" || e.key === "T") {
        if (!textInput && document.activeElement?.tagName !== "TEXTAREA" && document.activeElement?.tagName !== "INPUT") {
          setActiveTool("text");
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        handleUndo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "y") {
        e.preventDefault();
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") shiftKeyRef.current = false;
    };
    window.addEventListener("keydown", handler);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handler);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [open, textInput, canvasMenu, activeTool, drawingPoints, handleCanvasDblClick, readOnly]);

  const handleUndo = () => {
    if (readOnly) return;
    if (annotations.length === 0) return;
    const last = annotations[annotations.length - 1];
    setUndoStack((s) => [...s, last.id]);
    deleteAnnotation.mutate({ id: last.id, documentId: documentId, page_number: pageNum });
  };

  // ─── Annotation context menu actions ───

  const handleCopyValue = (ann: Annotation) => {
    const text = ann.label || ann.annotation_type;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleEditAnnotation = (ann: Annotation) => {
    if (readOnly) return;
    setEditingAnnotation(ann);
    setEditLabel(ann.label || "");
  };

  const commitEditAnnotation = () => {
    if (readOnly) return;
    if (!editingAnnotation) return;
    updateAnnotation.mutate({
      id: editingAnnotation.id,
      documentId: documentId,
      page_number: pageNum,
      label: editLabel,
    });
    setEditingAnnotation(null);
    setEditLabel("");
  };

  const handleChangeColor = (ann: Annotation, color: string) => {
    if (readOnly) return;
    updateAnnotation.mutate({
      id: ann.id,
      documentId: documentId,
      page_number: pageNum,
      color,
    });
  };

  const handleChangeThickness = (ann: Annotation, lw: number) => {
    if (readOnly) return;
    const newGeo = { ...ann.geometry, line_width: lw };
    updateAnnotation.mutate({
      id: ann.id,
      documentId: documentId,
      page_number: pageNum,
      geometry: newGeo,
    });
  };

  const handleDuplicate = (ann: Annotation) => {
    if (readOnly) return;
    const offset = 0.02;
    const newGeo = offsetGeometry(ann.geometry, offset, offset);
    saveAnnotation.mutate({
      documentId: documentId, ownerType, takeoff_group_id: activeGroupId,
      page_number: pageNum,
      annotation_type: ann.annotation_type,
      geometry: newGeo,
      label: ann.label ?? undefined,
      color: ann.color,
      measured_value: ann.measured_value ?? undefined,
      unit: ann.unit,
    });
  };

  // ─── Convert measurement unit ───
  const MEASUREMENT_ANNOTATION_TYPES = ["measurement_linear", "polyline", "measurement_area"];

  const handleConvertUnit = (ann: Annotation, targetUnit: string) => {
    if (readOnly) return;
    if (!ann.measured_value || !ann.unit || ann.unit === "px" || ann.unit === targetUnit) return;
    const isArea = ann.annotation_type === "measurement_area";
    const newValue = isArea
      ? convertArea(ann.measured_value, ann.unit, targetUnit)
      : convertLinear(ann.measured_value, ann.unit, targetUnit);
    const unitLabel = isArea ? `sq ${targetUnit}` : targetUnit;
    let newLabel = `${newValue.toFixed(2)} ${unitLabel}`;
    // For area annotations, preserve perimeter if present
    if (isArea && ann.label?.includes("| P:")) {
      const perimMatch = ann.label.match(/P:\s*([\d.]+)/);
      if (perimMatch) {
        const oldPerim = parseFloat(perimMatch[1]);
        const newPerim = convertLinear(oldPerim, ann.unit, targetUnit);
        newLabel += ` | P: ${newPerim.toFixed(2)} ${targetUnit}`;
      }
    }
    updateAnnotation.mutate({
      id: ann.id,
      documentId: documentId,
      page_number: pageNum,
      label: newLabel,
    });
    // Note: we update label only; measured_value and unit stay in original stored unit
    // so future conversions always compute from the original base
    toast.success(`Converted to ${unitLabel}`);
  };

  // Reset on close
  useEffect(() => {
    if (!open) {
      setPageNum(1);
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setActiveTool("select");
      setDrawingPoints([]);
      setCountTotal(0);
      setSignedUrl(null);
      setTextInput(null);
      setTextValue("");
      setIsDragging(false);
      setDragStart(null);
      setDragCurrent(null);
      setMovingAnn(null);
      setMoveStart(null);
      setMoveDelta({ x: 0, y: 0 });
      setCanvasMenu(null);
      setSelectedAnnId(null);
      pdfDocRef.current = null;
    }
  }, [open]);

  // Sync display unit to scale unit when scale changes
  useEffect(() => {
    if (scale?.unit) setDisplayUnit(scale.unit);
  }, [scale?.unit]);

  const scaleUnitLabel = scale?.unit || "px";

  // Determine cursor for canvas
  const getCanvasCursor = () => {
    if (movingAnn) return "grabbing";
    if (activeTool === "select") return "grab";
    if (readOnly) return "default";
    return "crosshair";
  };

  // ─── Shared annotation context menu content ───
  const renderAnnotationContextItems = (ann: Annotation) => (
    <>
      {ann.label && (
        <ContextMenuItem onClick={() => handleCopyValue(ann)} className="gap-2 text-xs">
          <Copy className="h-3.5 w-3.5" /> Copy Value
        </ContextMenuItem>
      )}
      {!readOnly && (
        <>
           <ContextMenuItem onClick={() => handleEditAnnotation(ann)} className="gap-2 text-xs">
            <Pencil className="h-3.5 w-3.5" /> Edit Label
          </ContextMenuItem>
          {MEASUREMENT_ANNOTATION_TYPES.includes(ann.annotation_type) && ann.unit && ann.unit !== "px" && (
            <ContextMenuSub>
              <ContextMenuSubTrigger className="gap-2 text-xs">
                <Ruler className="h-3.5 w-3.5" /> Convert Unit
              </ContextMenuSubTrigger>
              <ContextMenuSubContent className="p-1">
                {LINEAR_UNITS.map((u) => (
                  <ContextMenuItem
                    key={u}
                    disabled={u === ann.unit}
                    onClick={() => handleConvertUnit(ann, u)}
                    className="text-xs"
                  >
                    {ann.annotation_type === "measurement_area" ? `sq ${u}` : u}
                  </ContextMenuItem>
                ))}
              </ContextMenuSubContent>
            </ContextMenuSub>
          )}
          <ContextMenuItem onClick={() => handleDuplicate(ann)} className="gap-2 text-xs">
            <CopyPlus className="h-3.5 w-3.5" /> Duplicate
          </ContextMenuItem>
          <ContextMenuSub>
            <ContextMenuSubTrigger className="gap-2 text-xs">
              <Palette className="h-3.5 w-3.5" /> Color
            </ContextMenuSubTrigger>
            <ContextMenuSubContent className="p-2">
              <div className="grid grid-cols-4 gap-1">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    className="h-6 w-6 rounded-full border-2 transition-all"
                    style={{
                      backgroundColor: c,
                      borderColor: ann.color === c ? "hsl(var(--primary))" : "transparent",
                    }}
                    onClick={() => handleChangeColor(ann, c)}
                  />
                ))}
              </div>
            </ContextMenuSubContent>
          </ContextMenuSub>
          <ContextMenuSub>
            <ContextMenuSubTrigger className="gap-2 text-xs">
              <Minus className="h-3.5 w-3.5" /> Thickness
            </ContextMenuSubTrigger>
            <ContextMenuSubContent className="p-1">
              {LINE_WIDTHS.map((lw) => (
                <ContextMenuItem
                  key={lw}
                  onClick={() => handleChangeThickness(ann, lw)}
                  className="gap-2 text-xs"
                >
                  <div className="w-6 rounded-full bg-foreground" style={{ height: lw }} />
                  {lw}px
                </ContextMenuItem>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>
          <ContextMenuSeparator />
          <ContextMenuItem
            onClick={() => deleteAnnotation.mutate({ id: ann.id, documentId: documentId, page_number: pageNum })}
            className="gap-2 text-xs text-destructive focus:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </ContextMenuItem>
        </>
      )}
    </>
  );

  const isEmbedded = mode === "embedded";

  const visibleAnnotationCount = visibleAnnotations.length;

  const headerBar = (
    <div className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 border-b border-border bg-card shrink-0">
      <span className={cn(
        "truncate",
        isEmbedded
          ? "font-bold text-sm md:text-base max-w-[50vw] md:max-w-none"
          : "font-semibold text-xs md:text-sm max-w-[40vw] md:max-w-none"
      )}>
        {fileName}
      </span>
      {readOnly && (
        <Badge variant="secondary" className="text-[10px] gap-1 px-1.5 py-0 shrink-0 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
          Read-Only
        </Badge>
      )}
      {isEmbedded && visibleAnnotationCount > 0 && (
        <Badge variant="secondary" className="text-[10px] gap-1 px-1.5 py-0 shrink-0">
          <List className="h-2.5 w-2.5" />
          {visibleAnnotationCount}
        </Badge>
      )}
      {scale && !isMobile && (
        <Badge variant="outline" className="text-xs gap-1">
          <Scale className="h-3 w-3" />
          1 px = {(1 / scale.pixels_per_unit).toFixed(4)} {scale.unit}
        </Badge>
      )}
      <div className="flex items-center gap-1 ml-auto">
        {isEmbedded && !isMobile && (
          <span className="text-xs text-muted-foreground mr-2">{Math.round(zoom * 100)}%</span>
        )}
        {isPdf && (
          <>
            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={pageNum <= 1} onClick={() => setPageNum((p) => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className={cn("text-xs text-muted-foreground", isEmbedded && "font-medium")}>
              {isEmbedded ? `Sheet ${pageNum} of ${totalPages}` : `${pageNum} / ${totalPages}`}
            </span>
            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={pageNum >= totalPages} onClick={() => setPageNum((p) => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </>
        )}
        {!isEmbedded && onOpenChange && (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );

  const viewerContent = (
    <div className={cn("flex flex-col overflow-hidden", isEmbedded ? "h-full" : "h-screen w-screen")}>
      {headerBar}

      <div className={cn("flex flex-1 overflow-hidden", isMobile && "flex-col")}>
        {/* Desktop: vertical toolbar */}
        {!isMobile && (
          <TakeoffToolbar
            activeTool={activeTool}
            onToolChange={handleToolChange}
            activeColor={activeColor}
            onColorChange={setActiveColor}
            activeLineWidth={activeLineWidth}
            onLineWidthChange={setActiveLineWidth}
            onUndo={handleUndo}
            onRedo={() => {}}
            canUndo={!readOnly && annotations.length > 0}
            canRedo={false}
            onZoomIn={() => setZoom((z) => Math.min(5, z + 0.2))}
            onZoomOut={() => setZoom((z) => Math.max(0.2, z - 0.2))}
            hasScale={!!scale}
            annotations={annotations}
            scaleUnit={scaleUnitLabel}
            displayUnit={displayUnit}
            readOnly={readOnly}
          />
        )}

        {/* Canvas viewport */}
        <div
          ref={containerRef}
          className="flex-1 overflow-hidden bg-muted relative"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          onContextMenu={(e) => e.preventDefault()}
          style={{ cursor: getCanvasCursor() }}
        >
          {/* Scale calibration banner */}
          {!readOnly && !scale && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-destructive/10 border border-destructive/30 text-destructive text-xs font-medium px-3 py-1.5 rounded-md shadow-sm">
              <Scale className="h-3.5 w-3.5" />
              <span>Scale not calibrated — set a known distance before measuring</span>
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-[10px] border-destructive/30 text-destructive hover:bg-destructive/10"
                onClick={() => { setActiveTool("scale"); }}
              >
                Calibrate
              </Button>
            </div>
          )}
          <div
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: "0 0",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          >
            <canvas ref={baseCanvasRef} style={{ display: "block" }} />
            <canvas
              ref={overlayCanvasRef}
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              onClick={handleCanvasClick}
              onDoubleClick={handleCanvasDblClick}
              onMouseDown={handleOverlayMouseDown}
              onMouseMove={handleOverlayMouseMove}
              onMouseUp={handleOverlayMouseUp}
              onContextMenu={handleCanvasContextMenu}
            />
            {/* Inline text input — only in edit mode */}
            {!readOnly && textInput && (
              <textarea
                ref={textRef}
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                onBlur={commitText}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    commitText();
                  }
                  if (e.key === "Escape") {
                    setTextInput(null);
                    setTextValue("");
                  }
                }}
                className="absolute bg-transparent border-2 border-dashed rounded px-1 py-0.5 text-[18px] font-bold outline-none resize min-w-[80px] min-h-[28px]"
                style={{
                  left: textInput.x,
                  top: textInput.y,
                  color: activeColor,
                  borderColor: activeColor,
                  zIndex: 10,
                }}
                placeholder="Type here…"
              />
            )}
          </div>

          {/* Canvas context menu (floating) */}
          {canvasMenu && (
            <div
              className="fixed z-50 min-w-[10rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-80"
              style={{ left: canvasMenu.x, top: canvasMenu.y }}
              onClick={() => setCanvasMenu(null)}
            >
              {canvasMenu.ann ? (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground capitalize">
                    {canvasMenu.ann.annotation_type.replace(/_/g, " ")}
                  </div>
                  <div className="-mx-1 my-1 h-px bg-border" />
                  {canvasMenu.ann.label && (
                    <button className="relative flex w-full items-center rounded-sm px-2 py-1.5 text-xs hover:bg-accent gap-2" onClick={() => { handleCopyValue(canvasMenu.ann!); setCanvasMenu(null); }}>
                      <Copy className="h-3.5 w-3.5" /> Copy Value
                    </button>
                  )}
                  {!readOnly && (
                    <>
                       <button className="relative flex w-full items-center rounded-sm px-2 py-1.5 text-xs hover:bg-accent gap-2" onClick={() => { handleEditAnnotation(canvasMenu.ann!); setCanvasMenu(null); }}>
                        <Pencil className="h-3.5 w-3.5" /> Edit Label
                      </button>
                      {MEASUREMENT_ANNOTATION_TYPES.includes(canvasMenu.ann!.annotation_type) && canvasMenu.ann!.unit && canvasMenu.ann!.unit !== "px" && (
                        <>
                          <div className="px-2 py-1 text-[10px] text-muted-foreground">Convert Unit</div>
                          <div className="flex flex-wrap gap-1 px-2 pb-1">
                            {LINEAR_UNITS.map((u) => (
                              <button
                                key={u}
                                disabled={u === canvasMenu.ann!.unit}
                                className="px-1.5 py-0.5 text-[10px] rounded hover:bg-accent disabled:opacity-30"
                                onClick={(ev) => { ev.stopPropagation(); handleConvertUnit(canvasMenu.ann!, u); setCanvasMenu(null); }}
                              >
                                {canvasMenu.ann!.annotation_type === "measurement_area" ? `sq ${u}` : u}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                      <button className="relative flex w-full items-center rounded-sm px-2 py-1.5 text-xs hover:bg-accent gap-2" onClick={() => { handleDuplicate(canvasMenu.ann!); setCanvasMenu(null); }}>
                        <CopyPlus className="h-3.5 w-3.5" /> Duplicate
                      </button>
                      <div className="-mx-1 my-1 h-px bg-border" />
                      <div className="px-2 py-1 text-[10px] text-muted-foreground">Color</div>
                      <div className="flex gap-1 px-2 pb-1">
                        {COLORS.map((c) => (
                          <button
                            key={c}
                            className="h-4 w-4 rounded-full border-2 transition-all"
                            style={{
                              backgroundColor: c,
                              borderColor: canvasMenu.ann!.color === c ? "hsl(var(--primary))" : "transparent",
                            }}
                            onClick={(ev) => { ev.stopPropagation(); handleChangeColor(canvasMenu.ann!, c); setCanvasMenu(null); }}
                          />
                        ))}
                      </div>
                      <div className="px-2 py-1 text-[10px] text-muted-foreground">Thickness</div>
                      <div className="flex gap-1 px-2 pb-1 items-center">
                        {LINE_WIDTHS.map((lw) => (
                          <button
                            key={lw}
                            className="flex items-center justify-center h-5 w-5 rounded hover:bg-accent"
                            onClick={(ev) => { ev.stopPropagation(); handleChangeThickness(canvasMenu.ann!, lw); setCanvasMenu(null); }}
                            title={`${lw}px`}
                          >
                            <div className="w-3 rounded-full bg-foreground" style={{ height: lw }} />
                          </button>
                        ))}
                      </div>
                      <div className="-mx-1 my-1 h-px bg-border" />
                      <button
                        className="relative flex w-full items-center rounded-sm px-2 py-1.5 text-xs hover:bg-accent gap-2 text-destructive"
                        onClick={() => { deleteAnnotation.mutate({ id: canvasMenu.ann!.id, documentId: documentId, page_number: pageNum }); setCanvasMenu(null); }}
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">No annotation here</div>
              )}
            </div>
          )}
        </div>

        {/* Annotation sidebar — hidden on mobile, use Sheet */}
        {!isMobile && (
          <div className="w-72 border-l border-border bg-card shrink-0 flex flex-col">
            <div className="px-3 py-2 border-b border-border">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Annotations ({visibleAnnotationCount})
              </h3>
            </div>

            {/* Measurement summary — uses visible annotations */}
            {visibleAnnotationCount > 0 && (
              <div className="px-3 py-2 border-b border-border space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase">Summary</p>
                  {scaleUnitLabel !== "px" && (
                    <select
                      className="h-6 text-[10px] rounded border border-input bg-background px-1"
                      value={displayUnit}
                      onChange={(e) => setDisplayUnit(e.target.value)}
                    >
                      {LINEAR_UNITS.map((u) => (
                        <option key={u} value={u}>{u}</option>
                      ))}
                    </select>
                  )}
                </div>
                {(() => {
                  const linears = visibleAnnotations.filter((a) => a.annotation_type === "measurement_linear" || a.annotation_type === "polyline");
                  const areas = visibleAnnotations.filter((a) => a.annotation_type === "measurement_area");
                  const counts = visibleAnnotations.filter((a) => a.annotation_type === "measurement_count");
                  const rawL = linears.reduce((s, a) => s + (a.measured_value || 0), 0);
                  const rawA = areas.reduce((s, a) => s + (a.measured_value || 0), 0);
                  const tL = scaleUnitLabel !== "px" ? convertLinear(rawL, scaleUnitLabel, displayUnit) : rawL;
                  const tA = scaleUnitLabel !== "px" ? convertArea(rawA, scaleUnitLabel, displayUnit) : rawA;
                  const linU = scaleUnitLabel === "px" ? "px" : displayUnit;
                  const areaU = scaleUnitLabel === "px" ? "px²" : `sq ${displayUnit}`;
                  return (
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <div className="text-muted-foreground">Linear</div>
                        <div className="font-semibold">{tL > 0 ? `${tL.toFixed(1)} ${linU}` : "—"}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-muted-foreground">Area</div>
                        <div className="font-semibold">{tA > 0 ? `${tA.toFixed(1)} ${areaU}` : "—"}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-muted-foreground">Count</div>
                        <div className="font-semibold">{counts.length || "—"}</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <ScrollArea className="flex-1">
              <div className="p-2 space-y-0.5">
                {visibleAnnotations.map((ann) => (
                  <ContextMenu key={ann.id}>
                    <ContextMenuTrigger asChild>
                      <div
                        className={cn(
                          "flex items-center gap-2 p-2 rounded-md hover:bg-muted text-xs group cursor-pointer",
                          selectedAnnId === ann.id && "bg-muted ring-1 ring-primary/30"
                        )}
                        onClick={() => {
                          setSelectedAnnId(ann.id);
                          highlightAnnotation(ann.id);
                          panToAnnotation(ann);
                        }}
                      >
                        <div className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: ann.color }} />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium capitalize block truncate">
                            {ann.annotation_type.replace(/_/g, " ")}
                          </span>
                          {ann.label && (
                            <span className="text-muted-foreground block truncate text-[10px]">{ann.label}</span>
                          )}
                        </div>
                        {!readOnly && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 opacity-0 group-hover:opacity-100 shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteAnnotation.mutate({ id: ann.id, documentId: documentId, page_number: pageNum });
                            }}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </ContextMenuTrigger>
                    <ContextMenuContent className="w-52">
                      {renderAnnotationContextItems(ann)}
                    </ContextMenuContent>
                  </ContextMenu>
                ))}
                {visibleAnnotationCount === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    No annotations on this page
                  </p>
                )}
              </div>
            </ScrollArea>

            {/* Active tool hint */}
            <div className="px-3 py-2 border-t border-border text-xs text-muted-foreground">
              {readOnly && "View-only mode — zoom, pan, and inspect annotations"}
              {!readOnly && activeTool === "scale" && "Click two points to set scale • Shift = straight"}
              {!readOnly && activeTool === "measure_linear" && (scale ? "Click two points to measure • Shift = straight" : "⚠ Set scale first")}
              {!readOnly && activeTool === "measure_area" && "Click points, Enter to close • Click near start to auto-close • Shift = straight"}
              {!readOnly && activeTool === "polyline" && "Click points, Enter to finish • Shift = straight"}
              {!readOnly && activeTool === "count" && `Count: ${countTotal} — Click to add`}
              {!readOnly && activeTool === "text" && "Click to place text box"}
              {!readOnly && (activeTool === "arrow" || activeTool === "rectangle" || activeTool === "cloud" || activeTool === "highlight" || activeTool === "ellipse") &&
                "Click and drag to draw"}
              {!readOnly && activeTool === "select" && "Drag annotations to move • Right-click for options"}
            </div>
          </div>
        )}

        {/* Mobile: bottom toolbar + floating annotation toggle */}
        {isMobile && (
          <>
            <TakeoffToolbar
              activeTool={activeTool}
              onToolChange={handleToolChange}
              activeColor={activeColor}
              onColorChange={setActiveColor}
              activeLineWidth={activeLineWidth}
              onLineWidthChange={setActiveLineWidth}
              onUndo={handleUndo}
              onRedo={() => {}}
              canUndo={!readOnly && annotations.length > 0}
              canRedo={false}
              onZoomIn={() => setZoom((z) => Math.min(5, z + 0.2))}
              onZoomOut={() => setZoom((z) => Math.max(0.2, z - 0.2))}
              hasScale={!!scale}
              annotations={annotations}
              scaleUnit={scaleUnitLabel}
              displayUnit={displayUnit}
              horizontal
              readOnly={readOnly}
            />

            {/* Floating annotation sheet trigger */}
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute bottom-14 right-3 z-30 shadow-lg gap-1.5 text-xs"
                >
                  <List className="h-3.5 w-3.5" />
                  {visibleAnnotationCount > 0 && visibleAnnotationCount}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[60vh] p-0">
                <SheetHeader className="px-4 py-3 border-b border-border">
                  <SheetTitle className="text-sm">Annotations ({visibleAnnotationCount})</SheetTitle>
                </SheetHeader>
                <ScrollArea className="flex-1 h-[calc(60vh-52px)]">
                  <div className="p-3 space-y-1">
                    {visibleAnnotations.map((ann) => (
                      <div
                        key={ann.id}
                        className={cn(
                          "flex items-center gap-2 p-2 rounded-md hover:bg-muted text-xs cursor-pointer",
                          selectedAnnId === ann.id && "bg-muted ring-1 ring-primary/30"
                        )}
                        onClick={() => {
                          setSelectedAnnId(ann.id);
                          highlightAnnotation(ann.id);
                          panToAnnotation(ann);
                        }}
                      >
                        <div className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: ann.color }} />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium capitalize block truncate">
                            {ann.annotation_type.replace(/_/g, " ")}
                          </span>
                          {ann.label && (
                            <span className="text-muted-foreground block truncate text-[10px]">{ann.label}</span>
                          )}
                        </div>
                        {!readOnly && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteAnnotation.mutate({ id: ann.id, documentId: documentId, page_number: pageNum });
                            }}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {visibleAnnotationCount === 0 && (
                      <p className="text-xs text-muted-foreground text-center py-4">
                        No annotations on this page
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </SheetContent>
            </Sheet>
          </>
        )}
      </div>

      {/* Scale calibration modal — only in edit mode */}
      {!readOnly && showScaleInput && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 space-y-4 max-w-sm w-full mx-4 border border-border">
            <h3 className="font-semibold text-foreground">Calibrate Scale</h3>
            <p className="text-sm text-muted-foreground">Enter the real-world distance of the line you drew:</p>
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder={scaleUnit === "ft" || scaleUnit === "in" ? `e.g. 5'11" or 10.5` : "Distance"}
                value={scaleDistance}
                onChange={(e) => setScaleDistance(e.target.value)}
                className="flex-1"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleScaleSubmit()}
              />
              <select
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={scaleUnit}
                onChange={(e) => setScaleUnit(e.target.value)}
              >
                <option value="ft">ft</option>
                <option value="in">in</option>
                <option value="m">m</option>
                <option value="cm">cm</option>
                <option value="yd">yd</option>
                <option value="mm">mm</option>
              </select>
            </div>
            {(scaleUnit === "ft" || scaleUnit === "in") && (
              <p className="text-[10px] text-muted-foreground">Supports 5'11" or decimal values</p>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => { setShowScaleInput(false); setPendingScaleLine(null); }}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleScaleSubmit}>Set Scale</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit annotation label modal — only in edit mode */}
      {!readOnly && editingAnnotation && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 space-y-4 max-w-sm w-full mx-4 border border-border">
            <h3 className="font-semibold text-foreground">Edit Label</h3>
            <Input
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && commitEditAnnotation()}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setEditingAnnotation(null)}>Cancel</Button>
              <Button size="sm" onClick={commitEditAnnotation}>Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isEmbedded) return viewerContent;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[100vw] max-h-[100vh] w-screen h-screen p-0 gap-0 rounded-none border-0">
        <DialogTitle className="sr-only">Takeoff Viewer — {fileName}</DialogTitle>
        {viewerContent}
      </DialogContent>
    </Dialog>
  );
};
